// After invite / magic-link sign-in, prompt once to set a password (email-only users, no Google identity).
import { useCallback, useEffect, useState } from "react";
import { useAuth } from "@/contexts/AuthContext";
import { formatAuthPasswordError, passwordMeetsRelaxedPolicy, PASSWORD_POLICY_HINT } from "@/lib/auth/formatAuthPasswordError";
import { shouldOfferFirstTimePasswordSetup } from "@/lib/auth/firstTimePasswordSetup";
import { requireSupabase } from "@/lib/supabase";
import { PasswordFieldWithToggle } from "./PasswordFieldWithToggle";

type OfferState = "idle" | "checking" | "show" | "hide";

const MIN_PASSWORD_LEN = 8;

export function FirstTimePasswordSetupGate() {
  const auth = useAuth();
  const [offer, setOffer] = useState<OfferState>("idle");
  const [password, setPassword] = useState("");
  const [confirm, setConfirm] = useState("");
  const [busy, setBusy] = useState(false);
  const [formError, setFormError] = useState<string | null>(null);

  const sessionUserId = auth.session?.user?.id;
  const metaKey = JSON.stringify(auth.session?.user?.user_metadata ?? {});

  const evaluate = useCallback(async () => {
    if (
      auth.status === "no-supabase" ||
      auth.status === "idle" ||
      auth.status === "loading" ||
      !auth.session ||
      auth.impersonation.active
    ) {
      setOffer("hide");
      return;
    }
    setOffer("checking");
    try {
      const sb = requireSupabase();
      const { data, error } = await sb.auth.getUser();
      if (error || !data.user) {
        setOffer("hide");
        return;
      }
      const u = data.user;
      if (!shouldOfferFirstTimePasswordSetup(u)) {
        setOffer("hide");
        return;
      }
      setOffer("show");
    } catch {
      setOffer("hide");
    }
  }, [auth.status, auth.impersonation.active, sessionUserId, metaKey]);

  useEffect(() => {
    void evaluate();
  }, [evaluate]);

  const refreshAfterMetadata = useCallback(() => {
    auth.refreshProfile();
  }, [auth]);

  async function savePassword() {
    setFormError(null);
    if (password.length < MIN_PASSWORD_LEN) {
      setFormError(`Use at least ${MIN_PASSWORD_LEN} characters.`);
      return;
    }
    if (!passwordMeetsRelaxedPolicy(password)) {
      setFormError(PASSWORD_POLICY_HINT);
      return;
    }
    if (password !== confirm) {
      setFormError("Passwords do not match.");
      return;
    }
    setBusy(true);
    try {
      const sb = requireSupabase();
      const { error } = await sb.auth.updateUser({
        password,
        data: { password_setup_complete: true },
      });
      if (error) {
        setFormError(formatAuthPasswordError(error.message));
        return;
      }
      setPassword("");
      setConfirm("");
      setOffer("hide");
      refreshAfterMetadata();
    } catch (e) {
      setFormError(formatAuthPasswordError(e instanceof Error ? e.message : ""));
    } finally {
      setBusy(false);
    }
  }

  if (offer !== "show") return null;

  return (
    <div
      className="fixed inset-0 z-[200] flex items-center justify-center bg-black/50 p-4"
      role="dialog"
      aria-modal="true"
      aria-labelledby="first-time-password-title"
    >
      <div className="w-full max-w-md rounded-2xl border border-slate-200 bg-white p-5 shadow-xl dark:border-slate-600 dark:bg-slate-900">
        <h2 id="first-time-password-title" className="text-lg font-semibold text-slate-900 dark:text-slate-100">
          Finish your account
        </h2>
        <p className="mt-2 text-sm text-slate-600 dark:text-slate-300">
          You are signed in. Create a password for this email so you can open the app anytime without a new email link.
        </p>
        <p className="mt-2 text-xs text-slate-500 dark:text-slate-400">{PASSWORD_POLICY_HINT} At least {MIN_PASSWORD_LEN} characters.</p>

        <div className="mt-4 space-y-3">
          <PasswordFieldWithToggle
            label="New password"
            autoComplete="new-password"
            value={password}
            onChange={setPassword}
            disabled={busy}
          />
          <PasswordFieldWithToggle
            label="Confirm password"
            autoComplete="new-password"
            value={confirm}
            onChange={setConfirm}
            disabled={busy}
            onEnter={() => void savePassword()}
          />
        </div>

        {formError ? <p className="mt-3 text-sm text-rose-600 dark:text-rose-400">{formError}</p> : null}

        <div className="mt-5 flex justify-end">
          <button
            type="button"
            disabled={busy}
            onClick={() => void savePassword()}
            className="rounded-lg bg-indigo-600 px-4 py-2 text-sm font-semibold text-white hover:bg-indigo-700 disabled:opacity-50"
          >
            {busy ? "Saving…" : "Save password"}
          </button>
        </div>
      </div>
    </div>
  );
}
